import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const AppointmentFormDialog = ({ open, onClose, onSuccess, appointment = null }) => {
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState(appointment || {
    patient_id: '',
    soignant_id: '',
    start: '',
    end: '',
    type: 'consultation',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchPatients();
    }
  }, [open]);

  useEffect(() => {
    if (appointment) {
      setFormData(appointment);
    }
  }, [appointment]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (appointment) {
        await axios.put(`${API}/appointments/${appointment.id}`, formData);
      } else {
        await axios.post(`${API}/appointments`, formData);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Calculate end time based on appointment type
  const handleStartChange = (start) => {
    setFormData({ ...formData, start });
    
    if (start && !appointment) {
      const startDate = new Date(start);
      const duration = formData.type === 'perfusion' ? 90 : 30; // 90 min for perfusion, 30 min for consultation
      const endDate = new Date(startDate.getTime() + duration * 60000);
      const end = endDate.toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, end }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="appointment-form-dialog">
        <DialogHeader>
          <DialogTitle>{appointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}</DialogTitle>
          <DialogDescription>
            {appointment ? 'Modifiez les informations du rendez-vous' : 'Planifiez un nouveau rendez-vous'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="patient_id">Patient *</Label>
            <select
              id="patient_id"
              data-testid="patient-select"
              value={formData.patient_id}
              onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Sélectionner un patient</option>
              {patients.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.identite.prenom} {patient.identite.nom}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Type de rendez-vous *</Label>
            <select
              id="type"
              data-testid="type-select"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="consultation">Consultation</option>
              <option value="perfusion">Perfusion IV</option>
              <option value="suivi">Suivi</option>
              <option value="controle">Contrôle</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start">Date et heure de début *</Label>
              <Input
                id="start"
                type="datetime-local"
                data-testid="start-input"
                value={formData.start}
                onChange={(e) => handleStartChange(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end">Date et heure de fin *</Label>
              <Input
                id="end"
                type="datetime-local"
                data-testid="end-input"
                value={formData.end}
                onChange={(e) => setFormData({ ...formData, end: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="notes-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Notes supplémentaires..."
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="save-appointment-button"
              style={{
                background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
                color: 'white'
              }}
            >
              {loading ? 'Enregistrement...' : appointment ? 'Mettre à jour' : 'Créer le rendez-vous'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentFormDialog;
